import { z } from 'zod';

// Mirrors backend SignCare.Application.Sessions.Dtos.
// Status values: InProgress | PendingApproval | Completed | Rejected
// Channel values: Web | Mobile

export const sessionDtoSchema = z.object({
  id: z.string().uuid(),
  workflowId: z.string().uuid(),
  workflowName: z.string(),
  applicantName: z.string(),
  // The name a verification step returned, and the OTP-verified mobile. Null until
  // they exist — the list shows NA rather than falling back to `applicantName`, which
  // is only a creation-time label ("Applicant" for a public run).
  applicantFullName: z.string().nullable(),
  applicantMobile: z.string().nullable(),
  applicantRef: z.string().nullable(),
  status: z.string(),
  completionPercent: z.number(),
  channel: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // The list's two independent status axes.
  //
  // originalStatus = what OUR SYSTEM concluded, widened to the seven values the
  // table shows: NotStarted | InProgress | Locked | Expired | InReview | Approved
  // | Rejected. Derived server-side from `status` plus the run's lock and validity
  // state, so it is never out of step with what the applicant can actually do.
  //
  // verifiedStatus = what the TENANT ADMIN decided: NotApplicable | Approved |
  // Rejected | ResubmitRequested | ExceptionRequested. Independent of the above —
  // an admin approving a run the rules rejected shows Rejected on one axis and
  // Approved on the other, which is the audit trail, not a bug.
  //
  // Defaulted rather than required so a frontend deployed ahead of the backend
  // degrades to the old single-status view instead of failing to parse the page.
  originalStatus: z.string().default(''),
  verifiedStatus: z.string().default('NotApplicable'),
  // Null until they happen — rendered as NA, never as an epoch date.
  submittedAt: z.string().nullable().default(null),
  verifiedAt: z.string().nullable().default(null),
  // This run's steps in workflow order, for the icon flow under the workflow name.
  // Just the type and outcome — enough to pick an icon and tint it.
  steps: z
    .array(z.object({ position: z.number().int(), stepType: z.string(), status: z.string() }))
    .default([]),
  // Shared-link fields. referenceId = human label; publicToken forms /w/{token}.
  referenceId: z.string().default(''),
  publicToken: z.string().default(''),
  // Email the link was sent to (null when only shared as QR/common link).
  inviteEmail: z.string().nullable().default(null),
  // What the row is: a real run, or an INVITE emailed and never opened. Both appear in
  // this list — an unopened invite IS a Not Started run to an admin — but an invite has no
  // session to open, cannot be deleted by the session endpoint, and resends through the
  // invite endpoint. Defaulted so an older API (which sends only sessions) still parses.
  rowKind: z.enum(['Session', 'Invite']).default('Session'),
});

export type SessionDto = z.infer<typeof sessionDtoSchema>;

export const pagedSessionsSchema = z.object({
  items: z.array(sessionDtoSchema),
  totalCount: z.number(),
  page: z.number(),
  pageSize: z.number(),
  totalPages: z.number(),
});

// One enabled API for a step, ordered by priority (1 = primary, 2+ = fallbacks).
// Drives which input screen the session shows and the fallback order.
export const sessionStepApiBindingDtoSchema = z.object({
  catalogueEntryId: z.string().uuid(),
  path: z.string(),
  operationId: z.string(),
  summary: z.string(),
  priority: z.number().int(),
  // Console's top-level category for this API — the welcome screen groups by it.
  // OPTIONAL rather than defaulted: the runners' fallback bindings for legacy
  // snapshots have no catalogue row behind them, so there is genuinely no category
  // to state. Absent groups the step under "Other Verification" rather than
  // pretending it belongs somewhere.
  category: z.string().optional(),
});

export type SessionStepApiBindingDto = z.infer<typeof sessionStepApiBindingDtoSchema>;

export const sessionStepDtoSchema = z.object({
  id: z.string().uuid(),
  stepType: z.string(),
  displayName: z.string(),
  position: z.number().int(),
  status: z.string(),
  riskOutcome: z.string().nullable(),
  inputJson: z.string().nullable(),
  resultJson: z.string().nullable(),
  apiBindings: z.array(sessionStepApiBindingDtoSchema).default([]),
  // Attempt-limit state (Settings tab → Max Attempts / Auto-lock).
  attemptCount: z.number().default(0),
  lockedUntil: z.string().nullable().default(null),
  // True when Return-Data enables date_of_birth/age → the DOB field is required.
  requiresDob: z.boolean().default(false),
  // The ENABLED risk rules that were in force for this step, read from the workflow
  // snapshot the run was frozen against. `action` is the rule's CONFIGURATION
  // (Approve | Review | Reject), not a per-rule verdict — per-rule outcomes are not
  // stored, only the step's aggregate riskOutcome.
  riskRules: z.array(z.object({ ruleKey: z.string(), action: z.string() })).default([]),
});

export type SessionStepDto = z.infer<typeof sessionStepDtoSchema>;

export const sessionDetailDtoSchema = z.object({
  id: z.string().uuid(),
  workflowId: z.string().uuid(),
  workflowName: z.string(),
  applicantName: z.string(),
  applicantRef: z.string().nullable(),
  status: z.string(),
  completionPercent: z.number(),
  channel: z.string(),
  submittedByUserId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Fields the detail view's Session Details card needs. All DEFAULTED, deliberately: a
  // frontend deployed (or hot-reloaded) ahead of the API must degrade to "NA" in a few
  // rows rather than fail to parse the whole session — which is exactly what a required
  // field did to the sessions list.
  applicantFullName: z.string().nullable().default(null),
  applicantMobile: z.string().nullable().default(null),
  submittedAt: z.string().nullable().default(null),
  verifiedStatus: z.string().default('NotApplicable'),
  // The seven-value display status, resolved server-side by the same rules the list uses.
  // Null on the step-submit responses, which are built without a clock — readers fall back
  // to `status`.
  originalStatus: z.string().nullable().default(null),
  // The verdict's audit record. Admin-only: the API strips all four before any response
  // crosses the anonymous applicant boundary, so they are null in the runner by design.
  verifiedAt: z.string().nullable().default(null),
  verifiedNote: z.string().nullable().default(null),
  verifiedByName: z.string().nullable().default(null),
  completionPercentAtDecision: z.number().nullable().default(null),
  inviteEmail: z.string().nullable().default(null),
  // True when the session's validity window (Session Expiry) has already elapsed
  // — the runner shows the expired screen on load instead of after a Verify click.
  isExpired: z.boolean().default(false),
  steps: z.array(sessionStepDtoSchema),
  // Resolved applicant-journey branding (workflow override ?? org default).
  // Present on session load; null → the runner uses built-in defaults.
  customization: z
    .object({
      customizationJson: z.string().nullable(),
      logoUrl: z.string().nullable(),
    })
    .nullable()
    .optional(),
  // Applicant-facing run options (Workflow Settings → Options). Present on session
  // load; absent → the defaults below, which match a freshly created workflow.
  options: z
    .object({
      allowDesktopAccess: z.boolean().default(true),
      showProgressBar: z.boolean().default(false),
      // Already-resolved welcome copy: null → the runner uses its defaults.
      welcomeHeading: z.string().nullable().default(null),
      welcomeSubheading: z.string().nullable().default(null),
    })
    .nullable()
    .optional(),
  referenceId: z.string().default(''),
  publicToken: z.string().default(''),
  // Workflow Callback URL — where the applicant is redirected once the run
  // finishes. Present on session load; null when the workflow has none.
  completionRedirectUrl: z.string().nullable().default(null),
});

export type SessionDetailDto = z.infer<typeof sessionDetailDtoSchema>;

export interface StartSessionInput {
  workflowId: string;
  applicantName: string;
  applicantRef?: string | null;
  channel: string;
  /** Optional human label for the shared link (blank → backend uses the token). */
  referenceId?: string | null;
  /** Optional applicant email — when set, the invite email is sent to it. */
  email?: string | null;
}
